import React, { useEffect } from 'react';
import { TabStrip, TabStripTab } from '@progress/kendo-react-layout';
import { Input, Checkbox, NumericTextBox } from '@progress/kendo-react-inputs';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { useFormBuilder } from '../context/FormBuilderContext';
import type { Field, FieldWidth } from '../types';
import { FIELD_WIDTH_OPTIONS } from '../utils/fieldWidth';

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 'var(--kendo-spacing-3)' }}>
      <label
        style={{
          fontSize: 'var(--kendo-font-size-xs)',
          fontWeight: 'var(--kendo-font-weight-bold)',
          color: 'var(--kendo-color-subtle)',
          display: 'block',
          marginBottom: 'var(--kendo-spacing-1)',
        }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

function GeneralTab({ field, onChange, isEditable }: { field: Field; onChange: (p: Partial<Field>) => void; isEditable: boolean }) {
  return (
    <div style={{ padding: 'var(--kendo-spacing-3)' }}>
      <Row label="Label">
        <Input value={field.label} onChange={(e) => onChange({ label: String(e.value ?? '') })} disabled={!isEditable} style={{ width: '100%' }} />
      </Row>
      <Row label="Width">
        {/* Lets fields sit side by side within a section instead of always stacking full-width
            — the canvas/runtime render fields in a wrapping row, so siblings whose widths sum
            to <= 100% land on the same line. */}
        <DropDownList
          data={FIELD_WIDTH_OPTIONS}
          textField="label"
          dataItemKey="value"
          value={FIELD_WIDTH_OPTIONS.find((o) => o.value === (field.layout?.width ?? 'full'))}
          onChange={(e) => onChange({ layout: { width: e.value.value as FieldWidth } })}
          disabled={!isEditable}
          style={{ width: '100%' }}
        />
      </Row>
      <Row label="Handle (machine name)">
        <Input value={field.handle} onChange={(e) => onChange({ handle: String(e.value ?? '') })} disabled={!isEditable} style={{ width: '100%' }} />
      </Row>
      <Row label="Default value">
        <Input
          value={field.defaultValue ?? ''}
          onChange={(e) => onChange({ defaultValue: String(e.value ?? '') })}
          disabled={!isEditable}
          style={{ width: '100%' }}
        />
      </Row>
      <Checkbox
        label="Required"
        checked={field.isRequired}
        onChange={(e) => onChange({ isRequired: e.value === true })}
        disabled={!isEditable}
        style={{ marginBottom: 'var(--kendo-spacing-2)' }}
      />
      <br />
      <Checkbox
        label="Hidden"
        checked={field.isHidden}
        onChange={(e) => onChange({ isHidden: e.value === true })}
        disabled={!isEditable}
      />
    </div>
  );
}

function SettingsTab({
  field,
  onChange,
  allFields,
  PropertyEditor,
}: {
  field: Field;
  onChange: (p: Partial<Field>) => void;
  allFields: Field[];
  PropertyEditor?: React.ComponentType<{ field: Field; onChange: (p: Partial<Field>) => void; allFields: Field[] }>;
}) {
  if (!PropertyEditor) {
    return (
      <div style={{ padding: 'var(--kendo-spacing-3)', fontSize: 'var(--kendo-font-size-sm)', color: 'var(--kendo-color-subtle)' }}>
        This field type has no additional settings.
      </div>
    );
  }
  return (
    <div style={{ padding: 'var(--kendo-spacing-3)' }}>
      <PropertyEditor field={field} onChange={onChange} allFields={allFields} />
    </div>
  );
}

function ValidationTab({ field, onChange, isEditable }: { field: Field; onChange: (p: Partial<Field>) => void; isEditable: boolean }) {
  const v = field.validation ?? {};
  function patchValidation(p: Partial<Field['validation']>) {
    onChange({ validation: { ...v, ...p } });
  }
  return (
    <div style={{ padding: 'var(--kendo-spacing-3)' }}>
      <Row label="Min length">
        <NumericTextBox value={v.minLength ?? null} onChange={(e) => patchValidation({ minLength: e.value ?? undefined })} disabled={!isEditable} />
      </Row>
      <Row label="Max length">
        <NumericTextBox value={v.maxLength ?? null} onChange={(e) => patchValidation({ maxLength: e.value ?? undefined })} disabled={!isEditable} />
      </Row>
      <Row label="Min value">
        <NumericTextBox value={v.min ?? null} onChange={(e) => patchValidation({ min: e.value ?? undefined })} disabled={!isEditable} />
      </Row>
      <Row label="Max value">
        <NumericTextBox value={v.max ?? null} onChange={(e) => patchValidation({ max: e.value ?? undefined })} disabled={!isEditable} />
      </Row>
      <Row label="Pattern (regex)">
        <Input value={v.pattern ?? ''} onChange={(e) => patchValidation({ pattern: String(e.value ?? '') })} disabled={!isEditable} style={{ width: '100%' }} />
      </Row>
    </div>
  );
}

function ClinicalTab({ field, onChange, isEditable }: { field: Field; onChange: (p: Partial<Field>) => void; isEditable: boolean }) {
  const c = field.clinicalCoding ?? {};
  function patchCoding(p: Partial<Field['clinicalCoding']>) {
    onChange({ clinicalCoding: { ...c, ...p } });
  }
  return (
    <div style={{ padding: 'var(--kendo-spacing-3)' }}>
      <Row label="SNOMED CT concept ID">
        <Input value={c.snomedCid ?? ''} onChange={(e) => patchCoding({ snomedCid: String(e.value ?? '') })} disabled={!isEditable} style={{ width: '100%' }} />
      </Row>
      <Row label="LOINC code">
        <Input value={c.loincCode ?? ''} onChange={(e) => patchCoding({ loincCode: String(e.value ?? '') })} disabled={!isEditable} style={{ width: '100%' }} />
      </Row>
      <Row label="HL7 mapping">
        <Input value={c.hl7Mapping ?? ''} onChange={(e) => patchCoding({ hl7Mapping: String(e.value ?? '') })} disabled={!isEditable} style={{ width: '100%' }} />
      </Row>
      <Row label="Units">
        <Input value={c.units ?? ''} onChange={(e) => patchCoding({ units: String(e.value ?? '') })} disabled={!isEditable} style={{ width: '100%' }} />
      </Row>
    </div>
  );
}

export function PropertyInspector() {
  const { selectedField, updateField, fieldTypeRegistry, isEditable, document } = useFormBuilder();
  const [selectedTab, setSelectedTab] = React.useState(0);

  // Tabs are now rendered unconditionally (see SettingsTab) specifically so this index
  // always means the same tab regardless of field type. Still reset to General on every
  // field switch — landing on "Clinical Coding" for a field you just selected is confusing.
  useEffect(() => {
    setSelectedTab(0);
  }, [selectedField?.id]);

  if (!selectedField) {
    return (
      <div style={{ padding: 'var(--kendo-spacing-4)', fontSize: 'var(--kendo-font-size-sm)', color: 'var(--kendo-color-subtle)', height: '100%' }}>
        Select a field on the canvas to edit its properties.
      </div>
    );
  }

  const def = fieldTypeRegistry[selectedField.fieldType];
  const allFields = document.sections.flatMap((s) => s.fields);
  const onChange = (patch: Partial<Field>) => updateField(selectedField.id, patch);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="k-card-header">
        <div style={{ fontSize: 'var(--kendo-font-size-sm)', fontWeight: 'var(--kendo-font-weight-bold)' }}>
          {def?.icon} {def?.label ?? selectedField.fieldType}
        </div>
      </div>
      <TabStrip selected={selectedTab} onSelect={(e) => setSelectedTab(e.selected)} style={{ flex: 1, minHeight: 0 }}>
        <TabStripTab title="General">
          <GeneralTab field={selectedField} onChange={onChange} isEditable={isEditable} />
        </TabStripTab>
        <TabStripTab title="Settings">
          <SettingsTab field={selectedField} onChange={onChange} allFields={allFields} PropertyEditor={def?.PropertyEditor} />
        </TabStripTab>
        <TabStripTab title="Validation">
          <ValidationTab field={selectedField} onChange={onChange} isEditable={isEditable} />
        </TabStripTab>
        <TabStripTab title="Clinical Coding">
          <ClinicalTab field={selectedField} onChange={onChange} isEditable={isEditable} />
        </TabStripTab>
      </TabStrip>
    </div>
  );
}
